/**
 * Short-lived promise cache for hot read-only Firestore queries.
 *
 * Several homepage sections (and the pages they link to) issue the same list queries
 * independently on every mount — events three times per homepage load, articles and
 * limited games twice, plus a refetch on every route navigation. Sharing the in-flight
 * promise (and the resolved snapshot for a couple of minutes) collapses those duplicate
 * reads without touching the calling components.
 *
 * Only ever use this for list reads where a result up to TTL_MS stale is acceptable.
 * Never wrap writes or reads that feed a write.
 */
const cache = new Map<string, { promise: Promise<any>, expires: number }>();

const TTL_MS = 2 * 60 * 1000;

export const cachedFirestoreFetch = (key: string, loader: () => Promise<any>): Promise<any> => {
    const now = Date.now();
    const entry = cache.get(key);
    if (entry && entry.expires > now) {
        return entry.promise;
    }
    const promise = loader().catch((error: any) => {
        // A failed read must not be served from the cache for the next TTL window.
        cache.delete(key);
        throw error;
    });
    cache.set(key, { promise, expires: now + TTL_MS });
    return promise;
};
